import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  BookOpen,
  FlaskConical,
  Bot,
  LineChart,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Minus,
  CheckCircle2,
  Calculator,
  Brain,
  Network,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { COURSE, LAB_CASES, ACADEMY_UI, getLesson, getLabCase } from "../data/academyCourse";
import { getCasosUi } from "../data/casosEstudio";
import { VIZ, getViz } from "../data/academyViz";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { AudioReader } from "../components/AudioReader";
import { AIMentor } from "../components/AIMentor";
import { PremiumGate } from "../components/PremiumGate";
import { LessonQuiz } from "../components/LessonQuiz";
import { CourseProgress } from "../components/CourseProgress";
import { SimulatorRankCard } from "../components/SimulatorRankCard";
import { ComentariosAcademia } from "../components/academy/ComentariosAcademia";
import { BannerOferta } from "../components/academy/BannerOferta";
import { LearnEarnBanner } from "../components/academy/LearnEarnBanner";
import { ChapterRewardBar } from "../components/academy/ChapterRewardBar";
import { LearningProgress } from "../components/academy/LearningProgress";
import DiplomaModal from "../components/academia/DiplomaModal";
import { useAuth } from "../contexts/AuthContext";
import { QUIZZES } from "../data/academyQuizzes";

const badgeColors = {
  bull: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  bear: "border-red-400/30 bg-red-400/10 text-red-300",
  neut: "border-purple-400/30 bg-purple-400/10 text-purple-300",
};
const badgeIcons = { bull: TrendingUp, bear: TrendingDown, neut: Minus };

const AcademyPage = () => {
  const { lang } = useLanguage();
  const { isPro, trial, refreshUser, progress } = useAuth();
  const ui = ACADEMY_UI[lang] || ACADEMY_UI.en || ACADEMY_UI.es;
  // Each feature has its own 1-use trial. Once consumed, show paywall.
  const canUseTTS = isPro || (trial?.tts_remaining || 0) > 0;
  const canUseMentor = isPro || (trial?.chat_remaining || 0) > 0;

  const [view, setView] = useState("course"); // 'course' | 'lab'
  const [levelIdx, setLevelIdx] = useState(0);
  const [lessonIdx, setLessonIdx] = useState(0);

  // RE₿EL Proof™ — open diploma modal when a level just became complete.
  const [diplomaNivel, setDiplomaNivel] = useState(null);
  const prevLevelDoneRef = React.useRef(null);
  useEffect(() => {
    const keys = new Set(progress?.completed_keys || []);
    const levelDone = COURSE.map((lvl) =>
      lvl.lessons.every((l) => keys.has(l.viz))
    );
    if (prevLevelDoneRef.current) {
      for (let i = 0; i < levelDone.length; i++) {
        if (levelDone[i] && !prevLevelDoneRef.current[i]) {
          const nivel = i + 1;
          const seen = sessionStorage.getItem(`diploma_seen_lvl${nivel}`);
          if (!seen) {
            setDiplomaNivel(nivel);
            sessionStorage.setItem(`diploma_seen_lvl${nivel}`, "1");
          }
          break;
        }
      }
    }
    prevLevelDoneRef.current = levelDone;
  }, [progress?.completed_keys]);

  // Allow deep-linking to lab view via location state (e.g. from a case study).
  const location = useLocation();
  useEffect(() => {
    if (location?.state?.view === "lab") setView("lab");
  }, [location?.state?.view]);

  const lesson = useMemo(() => COURSE[levelIdx].lessons[lessonIdx], [levelIdx, lessonIdx]);
  const tLesson = useMemo(() => getLesson(lesson, lang), [lesson, lang]);
  const labCases = useMemo(() => LAB_CASES.map((c) => getLabCase(c, lang)), [lang]);

  const goNext = () => {
    if (lessonIdx < COURSE[levelIdx].lessons.length - 1) {
      setLessonIdx(lessonIdx + 1);
    } else if (levelIdx < COURSE.length - 1) {
      setLevelIdx(levelIdx + 1);
      setLessonIdx(0);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goPrev = () => {
    if (lessonIdx > 0) {
      setLessonIdx(lessonIdx - 1);
    } else if (levelIdx > 0) {
      const prev = levelIdx - 1;
      setLevelIdx(prev);
      setLessonIdx(COURSE[prev].lessons.length - 1);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ttsText = `${tLesson.title}. ${tLesson.sub}. ${tLesson.bodyHtml.replace(/<[^>]+>/g, " ")}. ${ui.concepts}: ${tLesson.concepts.join(", ")}.`;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      {diplomaNivel != null && (
        <DiplomaModal nivel={diplomaNivel} onClose={() => setDiplomaNivel(null)} />
      )}
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,140,0,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(168,85,247,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_70%,rgba(0,229,255,0.10),transparent_55%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      {/* HEADER */}
      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link to="/" className="group inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white" data-testid="academy-back-home">
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.back}</span>
          </Link>

          <div className="flex items-center gap-3">
            <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
            <div className="hidden sm:block">
              <h1 className="font-display text-xs font-black uppercase tracking-[0.25em] text-white md:text-sm">
                {ui.page_title}
              </h1>
              <p className="text-[10px] text-white/40">{ui.page_subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/kids"
              data-testid="academy-kids-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/40 bg-gradient-to-r from-orange-400/15 to-emerald-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300 transition-colors hover:from-orange-400/25 hover:to-emerald-400/20"
              title="RE₿EL Kids Academy"
            >
              🎮 <span className="hidden sm:inline">RE₿EL Kids</span>
            </Link>
            <SimulatorRankCard />
            <LanguageSwitcher />
          </div>
        </div>

        {/* Tabs */}
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 pb-3 md:px-10">
          {[
            { id: "course", label: ui.tab_course, Icon: BookOpen },
            { id: "lab", label: ui.tab_lab, Icon: FlaskConical },
            { id: "mentor", label: ui.tab_mentor, Icon: Bot },
          ].map((tab) => {
            const active = view === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                data-testid={`academy-tab-${tab.id}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-all ${
                  active
                    ? "border-orange-400/60 bg-orange-500/15 text-orange-200 shadow-[0_0_20px_rgba(255,140,0,0.3)]"
                    : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/30 hover:text-white"
                }`}
              >
                <tab.Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
          <Link
            to="/academy/patrones"
            data-testid="academy-tab-patrones"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-all hover:border-white/30 hover:text-white"
          >
            <LineChart className="h-3.5 w-3.5" />
            {({es:"Patrones",en:"Patterns",sk:"Vzory",de:"Muster","zh-CN":"图表形态"})[lang] || "Patterns"}
            <span className="ml-1 inline-flex items-center justify-center rounded-md bg-orange-400/20 px-1.5 py-0 text-[9px] font-black text-orange-300">
              16
            </span>
          </Link>
          <Link
            to="/academy/calculadora"
            data-testid="academy-tab-calculadora"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-all hover:border-white/30 hover:text-white"
          >
            <Calculator className="h-3.5 w-3.5" />
            {({es:"Calculadora",en:"Calculator",sk:"Kalkulačka",de:"Rechner","zh-CN":"计算器"})[lang] || "Calculator"}
          </Link>
          <Link
            to="/academy/glosario"
            data-testid="academy-tab-glosario"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-all hover:border-white/30 hover:text-white"
          >
            <BookOpen className="h-3.5 w-3.5" />
            {({es:"Glosario",en:"Glossary",sk:"Slovník",de:"Glossar","zh-CN":"术语表"})[lang] || "Glossary"}
            <span className="ml-1 inline-flex items-center justify-center rounded-md bg-orange-400/20 px-1.5 py-0 text-[9px] font-black text-orange-300">
              35+
            </span>
          </Link>
          <Link
            to="/academy/estrategias"
            data-testid="academy-tab-estrategias"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-all hover:border-white/30 hover:text-white"
          >
            <FlaskConical className="h-3.5 w-3.5" />
            {({es:"Estrategias",en:"Strategies",sk:"Stratégie",de:"Strategien","zh-CN":"策略"})[lang] || "Strategies"}
          </Link>
          <Link
            to="/academy/psicologia"
            data-testid="academy-tab-psicologia"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-all hover:border-white/30 hover:text-white"
          >
            <Brain className="h-3.5 w-3.5" />
            {({es:"Psicología",en:"Psychology",sk:"Psychológia",de:"Psychologie","zh-CN":"心理学"})[lang] || "Psychology"}
          </Link>
          <Link
            to="/academy/onchain"
            data-testid="academy-tab-onchain"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-all hover:border-white/30 hover:text-white"
          >
            <Network className="h-3.5 w-3.5" />
            On-Chain
          </Link>
        </div>
      </header>

      {/* CONTENT */}
      <div className="relative z-10">
        {/* Banner Oferta — additive, auto-hides for PRO/expired/dismissed */}
        <div className="mx-auto max-w-7xl px-4 md:px-10">
          <LearnEarnBanner />
          <BannerOferta />
          <details className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur" data-testid="learning-progress-details">
            <summary className="cursor-pointer list-none px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white/70 transition hover:text-white">
              ✦ {({es:"Mi progreso Learn-to-Earn",en:"My Learn-to-Earn progress",sk:"Môj Learn-to-Earn pokrok",de:"Mein Learn-to-Earn-Fortschritt","zh-CN":"我的 Learn-to-Earn 进度"})[lang] || "My Learn-to-Earn progress"}
            </summary>
            <div className="border-t border-white/10 p-4">
              <LearningProgress />
            </div>
          </details>
        </div>
        <AnimatePresence mode="wait">
          {view === "course" && (
            <motion.div
              key="course"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="mx-auto grid max-w-7xl gap-6 px-4 py-8 lg:grid-cols-12 lg:px-10"
            >
              {/* SIDEBAR — Levels & lessons */}
              <aside className="lg:col-span-4 xl:col-span-3">
                <div className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur-xl">
                  <CourseProgress labels={ui} />
                  {COURSE.map((level, li) => {
                    const open = li === levelIdx;
                    return (
                      <div key={li} className="mb-1">
                        <button
                          onClick={() => {
                            setLevelIdx(li);
                            setLessonIdx(0);
                          }}
                          className={`group flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                            open
                              ? "bg-gradient-to-r from-orange-500/15 to-transparent ring-1 ring-orange-400/30"
                              : "hover:bg-white/5"
                          }`}
                          data-testid={`academy-level-${li}`}
                        >
                          <span
                            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-black ${
                              open
                                ? "bg-orange-400 text-black"
                                : "border border-white/10 bg-black text-white/50"
                            }`}
                          >
                            {String(li + 1).padStart(2, "0")}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
                              {ui.level_label} {li + 1}
                            </div>
                            <div
                              className={`text-sm font-bold ${
                                open ? "text-orange-200" : "text-white"
                              }`}
                            >
                              {ui.levels[li]}
                            </div>
                            <div className="text-xs text-white/40">{ui.level_subs[li]}</div>
                          </div>
                        </button>
                        {open && (
                          <div className="mb-2 ml-3 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                            {level.lessons.map((les, lj) => {
                              const active = lj === lessonIdx;
                              const lt = les.i18n[lang] || les.i18n.es;
                              const lessonDone = progress?.completed_keys?.includes(les.viz);
                              return (
                                <button
                                  key={lj}
                                  onClick={() => setLessonIdx(lj)}
                                  className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-xs transition-all ${
                                    active
                                      ? "bg-cyan-500/10 text-cyan-200 ring-1 ring-cyan-400/30"
                                      : lessonDone
                                      ? "text-emerald-200/80 hover:bg-emerald-500/5 hover:text-emerald-100"
                                      : "text-white/55 hover:bg-white/5 hover:text-white"
                                  }`}
                                  data-testid={`academy-lesson-${li}-${lj}`}
                                >
                                  {lessonDone ? (
                                    <CheckCircle2 className="mt-1 h-3 w-3 shrink-0 text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.7)]" />
                                  ) : (
                                    <span
                                      className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                                        active
                                          ? "bg-cyan-400 shadow-[0_0_8px_rgba(0,229,255,0.8)]"
                                          : "bg-white/20"
                                      }`}
                                    />
                                  )}
                                  <span className="flex-1 min-w-0">
                                    <span className="block font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">
                                      L{String(lj + 1).padStart(2, "0")}{les.category ? ` · ${les.category}` : ""}
                                    </span>
                                    <span className="line-clamp-2 leading-tight">{lt.title}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </aside>

              {/* LESSON CONTENT */}
              <main className="lg:col-span-8 xl:col-span-9">
                <motion.div
                  key={`${levelIdx}-${lessonIdx}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Lesson header */}
                  <div className="mb-6">
                    <div className="mb-3 inline-flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1.5 backdrop-blur">
                        <Sparkles className="h-3 w-3 text-cyan-300" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-200">
                          {ui.levels[levelIdx]} · {ui.lesson_label} {String(lessonIdx + 1).padStart(2, "0")}/{String(COURSE[levelIdx].lessons.length).padStart(2, "0")}
                        </span>
                      </span>
                      {lesson.category && (
                        <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-400/5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-200">
                          {lesson.category}
                        </span>
                      )}
                    </div>
                    <h2 className="font-display text-3xl font-black tracking-tighter text-white md:text-5xl">
                      {tLesson.title}
                    </h2>
                    <p className="mt-2 text-base text-white/50 md:text-lg">{tLesson.sub}</p>

                    {/* Audio reader (TTS) — 1 free use, then paywall */}
                    <div className="mt-5">
                      {canUseTTS ? (
                        <AudioReader
                          text={ttsText}
                          lang={lang}
                          onAfterPlay={refreshUser}
                          trialRemaining={isPro ? null : (trial?.tts_remaining || 0)}
                          labels={{
                            listen: ui.listen,
                            pause: ui.pause,
                            stop: ui.stop,
                            playing: ui.playing,
                            paused: ui.paused,
                            loading: ui.loading,
                            retry: ui.retry,
                            trial_one: ui.trial_one_listen,
                          }}
                        />
                      ) : (
                        <PremiumGate feature="listen" blur={false} trialUsed />
                      )}
                    </div>
                  </div>

                  {/* Viz panel */}
                  <div className="mb-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-950 to-black/60 backdrop-blur">
                    <div className="border-b border-white/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      ◈ {ui.visual}
                    </div>
                    <div
                      className="w-full p-4"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getViz(lesson.viz)) }}
                    />
                  </div>

                  {/* Body */}
                  <article
                    className="lesson-body prose prose-invert max-w-none rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-md md:p-8"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tLesson.bodyHtml) }}
                  />

                  {/* Learn-to-Earn reward bar (PRO only) */}
                  <ChapterRewardBar
                    lessonKey={lesson.viz}
                    wordCount={Math.max(800, (tLesson.bodyHtml || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length)}
                    level={levelIdx}
                  />

                  {/* Concepts */}
                  <div className="mt-6 rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-400/[0.04] to-transparent p-5 backdrop-blur">
                    <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300">
                      ◈ {ui.concepts}
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {tLesson.concepts.map((c, i) => (
                        <div
                          key={i}
                          className="rounded-lg border border-amber-300/20 bg-amber-400/5 px-3 py-2 text-center text-xs text-amber-200"
                        >
                          {c}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Lesson quiz */}
                  {QUIZZES[lesson.viz] && (QUIZZES[lesson.viz][lang] || QUIZZES[lesson.viz].es) && (
                    <div className="mt-6">
                      <LessonQuiz
                        lessonKey={lesson.viz}
                        questions={QUIZZES[lesson.viz][lang] || QUIZZES[lesson.viz].es}
                        labels={ui}
                      />
                    </div>
                  )}

                  {/* Nav buttons */}
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                      onClick={goPrev}
                      disabled={levelIdx === 0 && lessonIdx === 0}
                      data-testid="academy-prev-btn"
                      className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white/70 transition-all hover:border-white/30 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />
                      {ui.prev}
                    </button>
                    <button
                      onClick={goNext}
                      disabled={
                        levelIdx === COURSE.length - 1 &&
                        lessonIdx === COURSE[levelIdx].lessons.length - 1
                      }
                      data-testid="academy-next-btn"
                      className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-2.5 text-xs font-black uppercase tracking-[0.2em] text-black shadow-[0_0_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {ui.next}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </motion.div>
              </main>
            </motion.div>
          )}
          {view === "lab" && (
            <motion.div
              key="lab"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="mx-auto max-w-7xl px-4 py-10 md:px-10"
            >
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-400/5 px-3 py-1.5 backdrop-blur">
                <FlaskConical className="h-3.5 w-3.5 text-purple-300" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-purple-200">
                  {ui.lab_title}
                </span>
              </div>
              <h2 className="font-display text-3xl font-black tracking-tighter text-white md:text-5xl">
                {ui.lab_title}
              </h2>
              <p className="mt-2 max-w-2xl text-base text-white/55">{ui.lab_sub}</p>

              <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {labCases.map((c, i) => {
                  const BadgeIcon = badgeIcons[c.badge] || Minus;
                  const casosUi = getCasosUi(lang);
                  const hasSlug = !!c.slug;
                  const cardInner = (
                    <>
                      <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                      <div className="mb-3 flex items-center justify-between">
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
                          ◈ {ui.case_study}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${badgeColors[c.badge] || badgeColors.neut}`}
                        >
                          <BadgeIcon className="h-2.5 w-2.5" />
                          {c.tag}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold leading-snug text-white md:text-base">{c.title}</h3>
                      <p className="mt-2 text-xs text-white/55 md:text-sm">{c.desc}</p>
                      {hasSlug ? (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-orange-300 transition-transform group-hover:translate-x-0.5">
                          {casosUi.cta_card}
                          <ArrowRight className="h-3 w-3" />
                        </div>
                      ) : (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300/80">
                          <Sparkles className="h-3 w-3" />
                          {ui.coming}
                        </div>
                      )}
                    </>
                  );
                  const cardClass =
                    "group relative block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition-colors hover:border-orange-400/30";
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.6, delay: i * 0.04 }}
                      whileHover={{ y: -4 }}
                      data-testid={`academy-lab-case-${i}`}
                    >
                      {hasSlug ? (
                        <Link to={`/academy/lab-mercado/${c.slug}`} className={cardClass}>
                          {cardInner}
                        </Link>
                      ) : (
                        <div className={cardClass}>{cardInner}</div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
          {view === "mentor" && (
            <motion.div
              key="mentor"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="mx-auto max-w-7xl px-4 py-10 md:px-10"
            >
              {canUseMentor ? (
                <AIMentor
                  lang={lang}
                  labels={ui}
                  lessonTitle={tLesson.title}
                  lessonLevel={ui.levels[levelIdx]}
                  onAfterMessage={refreshUser}
                  trialRemaining={isPro ? null : (trial?.chat_remaining || 0)}
                />
              ) : (
                <PremiumGate feature="mentor" blur={false} trialUsed />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Separator + Community Comments — additive section */}
      <hr className="my-12 border-t border-white/5" />
      <ComentariosAcademia />

      {/* Footer mini */}
      <footer className="relative z-10 border-t border-white/5 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-2 px-4 text-center md:px-10">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-orange-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              {ui.page_title} · {ui.page_subtitle}
            </span>
          </div>
          <p className="text-[10px] text-white/30">
            © {new Date().getFullYear()} REBEL · A REBEL ecosystem property
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AcademyPage;
